/** Projects owns its state and behavior; dependencies are passed in by main. */
import {
  PROJECT_COLLABORATORS,
  PROJECT_ARCHIVE_DISCIPLINES,
  PROJECT_ARCHIVE_META,
} from './project-data.js';

export function register(app) {
  const { projects, layout, preferences } = app;
  const showcasePromises = new WeakMap();
  let activeShowcase = null;
  const warmedCovers = new Map();
  projects.warmShowcaseCover = function warmShowcaseCover(view) {
    const wrap = view?.querySelector?.('.project-detail-wrap');
    const first = JSON.parse(wrap?.dataset.showcaseMedia || '[]')[0];
    if (!first || first.type !== 'image' || warmedCovers.has(first.src)) return;
    const image = new Image();
    image.fetchPriority = 'high';
    image.decoding = 'async';
    image.src = first.src;
    warmedCovers.set(first.src, image);
  };

  // Creating every project carousel on the home page would fetch the full
  // portfolio and leave timers running in hidden views. Build only on demand.
  projects.ensureShowcase = function ensureShowcase(view) {
    if (!view) return Promise.resolve(null);
    if (!showcasePromises.has(view)) {
      const pending = projects.createProjectShowcase(view).catch((error) => {
        showcasePromises.delete(view);
        throw error;
      });
      showcasePromises.set(view, pending);
    }
    return showcasePromises.get(view);
  };

  projects.activateShowcase = function activateShowcase(showcase) {
    if (activeShowcase && activeShowcase !== showcase) activeShowcase.pause();
    activeShowcase = showcase;
    if (showcase) showcase.resume();
  };

  projects.pauseActiveShowcase = function pauseActiveShowcase() {
    if (activeShowcase) activeShowcase.pause();
    activeShowcase = null;
  };
  projects.PROJECT_COLLABORATORS = PROJECT_COLLABORATORS;
  projects.PROJECT_ARCHIVE_DISCIPLINES = PROJECT_ARCHIVE_DISCIPLINES;
  projects.PROJECT_ARCHIVE_META = PROJECT_ARCHIVE_META;
  projects.getProjectCollaborator = function getProjectCollaborator(projectId) {
    return (
      projects.PROJECT_COLLABORATORS[String(projectId || '').replace(/^view-project-/, '')] || null
    );
  };

  projects.getProjectArchiveSlug = function getProjectArchiveSlug(projectId) {
    return String(projectId || '').replace(/^view-project-/, '');
  };

  projects.getProjectArchiveMeta = function getProjectArchiveMeta(projectId) {
    return projects.PROJECT_ARCHIVE_META[projects.getProjectArchiveSlug(projectId)] || null;
  };

  projects.localizeProjectArchiveValue = function localizeProjectArchiveValue(
    value,
    lang = preferences.currentLang,
  ) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value[lang] || value.sk || value.en || '';
  };

  projects.setArchiveLocalizedText = function setArchiveLocalizedText(element, value) {
    if (!element) return;
    const sk = projects.localizeProjectArchiveValue(value, 'sk');
    const en = projects.localizeProjectArchiveValue(value, 'en');
    element.classList.add('lang-text');
    element.dataset.sk = sk;
    element.dataset.en = en;
    element.textContent = projects.localizeProjectArchiveValue(value, preferences.currentLang);
  };

  projects.getProjectArchiveDiscipline = function getProjectArchiveDiscipline(
    meta,
    lang = preferences.currentLang,
  ) {
    return projects.localizeProjectArchiveValue(
      meta.category || projects.PROJECT_ARCHIVE_DISCIPLINES[meta.discipline] || meta.discipline,
      lang,
    );
  };

  projects.getProjectArchiveKicker = function getProjectArchiveKicker(
    meta,
    lang = preferences.currentLang,
  ) {
    return [
      meta.code,
      projects.getProjectArchiveDiscipline(meta, lang),
      !meta.years?.length && meta.year,
      projects.localizeProjectArchiveValue(meta.location, lang),
      projects.localizeProjectArchiveValue(meta.typology, lang),
    ]
      .filter(Boolean)
      .join(' \xB7 ');
  };

  projects.getProjectArchiveCollaboratorLabel = function getProjectArchiveCollaboratorLabel(
    collaborator,
    lang = preferences.currentLang,
  ) {
    if (!collaborator) return '';
    return lang === 'en' ? `Collaboration with ${collaborator}` : `Spolupráca s ${collaborator}`;
  };

  projects.getProjectSlugFromCard = function getProjectSlugFromCard(card) {
    return card.dataset.projectId || '';
  };

  projects.setProjectArchiveMetaContent = function setProjectArchiveMetaContent(
    container,
    meta,
    collaborator,
  ) {
    if (!container || !meta) return;
    container.textContent = '';
    const kicker = document.createElement('span');
    kicker.className = 'project-archive-kicker lang-text';
    projects.setArchiveLocalizedText(kicker, {
      sk: projects.getProjectArchiveKicker(meta, 'sk'),
      en: projects.getProjectArchiveKicker(meta, 'en'),
    });
    container.appendChild(kicker);
    if (collaborator) {
      const collab = document.createElement('span');
      collab.className = 'project-archive-collab lang-text';
      projects.setArchiveLocalizedText(collab, {
        sk: projects.getProjectArchiveCollaboratorLabel(collaborator, 'sk'),
        en: projects.getProjectArchiveCollaboratorLabel(collaborator, 'en'),
      });
      container.appendChild(collab);
    }
  };

  projects.setProjectArchiveTitle = function setProjectArchiveTitle(titleElement, meta) {
    if (!titleElement || !meta || !meta.title) return;
    projects.setArchiveLocalizedText(titleElement, meta.title);
  };


  projects.applyProjectArchiveLabels = function applyProjectArchiveLabels() {
    document.querySelectorAll('.project-card').forEach((card) => {
      const slug = projects.getProjectSlugFromCard(card);
      const meta = projects.getProjectArchiveMeta(slug);
      if (!meta) return;
      projects.setProjectArchiveMetaContent(
        card.querySelector('.project-meta'),
        meta,
        projects.getProjectCollaborator(slug),
      );
      projects.setProjectArchiveTitle(card.querySelector('.project-title'), meta);
    });
    document.querySelectorAll('.project-detail-view').forEach((view) => {
      const meta = projects.getProjectArchiveMeta(view.id);
      if (!meta) return;
      projects.setProjectArchiveTitle(view.querySelector('.project-detail-title'), meta);
    });
    document.querySelectorAll('.project-detail-nav-link').forEach((link) => {
      const slug = projects.getProjectSlugFromCard(link);
      const meta = projects.getProjectArchiveMeta(slug);
      if (!meta) return;
      projects.setProjectArchiveMetaContent(
        link.querySelector('.project-detail-nav-meta'),
        meta,
        projects.getProjectCollaborator(slug),
      );
      projects.setProjectArchiveTitle(link.querySelector('.project-detail-nav-title'), meta);
    });
  };

  projects.getProjectCollaboratorLogo = function getProjectCollaboratorLogo(projectId) {
    const collaborator = projects.getProjectCollaborator(projectId);
    if (/beionde/i.test(collaborator || '')) {
      return {
        src: 'assets/icons/brand/beionde_logo.png',
        alt: 'Beionde Architekti',
      };
    }
    if (/kubis/i.test(collaborator || '')) {
      return {
        src: 'assets/icons/brand/kubis-symbol.svg',
        alt: 'Kubis Architekti',
      };
    }
    return null;
  };

  projects.createProjectShowcase = async function createProjectShowcase(view) {
    const PROJECT_SHOWCASE_MIN_RATIO = 1.6;
    function getProjectShowcaseMinRatio(wrap) {
      const configured = Number(wrap?.dataset.showcaseMinRatio);
      return Number.isFinite(configured) && configured > 0
        ? configured
        : PROJECT_SHOWCASE_MIN_RATIO;
    }
    function getProjectStudioLogo(viewId) {
      return projects.getProjectCollaboratorLogo(viewId);
    }
    function getMediaSource(el, isVideo) {
      let src = el.getAttribute('src');
      if (!src && isVideo) {
        const source = el.querySelector('source');
        src = source ? source.getAttribute('src') : '';
      }
      return src || '';
    }
    function getMediaIdentityKey(src) {
      const normalizedSrc = String(src || '').replace(/\\/g, '/');
      if (normalizedSrc === 'assets/images/projects/pochlieb/D5_Image_20231123_113203 copy.jpg') {
        return 'assets/images/projects/pochlieb/D5_Image_20231123_113203 copy.jpg';
      }
      return normalizedSrc;
    }
    function loadImageRatio(src) {
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
          resolve(
            image.naturalWidth > 0 && image.naturalHeight > 0
              ? image.naturalWidth / image.naturalHeight
              : 0,
          );
        };
        image.onerror = () => resolve(0);
        image.src = src;
      });
    }
    function loadVideoRatio(src) {
      return new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        video.onloadedmetadata = () => {
          resolve(
            video.videoWidth > 0 && video.videoHeight > 0
              ? video.videoWidth / video.videoHeight
              : 0,
          );
        };
        video.onerror = () => resolve(0);
        video.src = src;
      });
    }
    async function getElementAspectRatio(el, type, src) {
      if (type === 'image') {
        if (el.naturalWidth > 0 && el.naturalHeight > 0) {
          return el.naturalWidth / el.naturalHeight;
        }
        return loadImageRatio(src);
      }
      if (el.videoWidth > 0 && el.videoHeight > 0) {
        return el.videoWidth / el.videoHeight;
      }
      return loadVideoRatio(src);
    }
    const wrap = view.querySelector('.project-detail-wrap');
    const heroMedia = view.querySelector('.project-detail-image img, .project-detail-image video');
    const galleryMedia = Array.from(
      view.querySelectorAll('.detail-gallery img, .detail-gallery video'),
    );
    const minRatio = getProjectShowcaseMinRatio(wrap);
    if (!wrap || !heroMedia) return;
    const preparedMedia = wrap.dataset.showcaseMedia && JSON.parse(wrap.dataset.showcaseMedia);
    const mediaCandidates =
      preparedMedia ||
      (await Promise.all(
        [heroMedia, ...galleryMedia].filter(Boolean).map(async (el) => {
          const type = el.tagName.toLowerCase() === 'video' ? 'video' : 'image';
          const src = getMediaSource(el, type === 'video');
          if (!src) return null;
          const ratio = await getElementAspectRatio(el, type, src);
          return {
            type,
            src,
            alt: el.getAttribute('alt') || '',
            ratio,
          };
        }),
      ));
    const seenMediaSources = new Set();
    const media = mediaCandidates
      .filter(
        (item, index) =>
          item && item.src && (preparedMedia || index === 0 || item.ratio >= minRatio),
      )
      .filter((item) => {
        const mediaKey = getMediaIdentityKey(item.src);
        if (seenMediaSources.has(mediaKey)) return false;
        seenMediaSources.add(mediaKey);
        return true;
      })
      .map(({ type, src, alt }) => ({
        type,
        src,
        alt,
      }));
    if (!media.length) return;
    const studioLogo = getProjectStudioLogo(view.id);
    const showcase = document.createElement('section');
    showcase.className = 'project-showcase';
    const brandClass =
      studioLogo && /kubis/i.test(studioLogo.alt)
        ? ' project-showcase-brand is-kubis'
        : ' project-showcase-brand';
    const brandLabelText =
      document.documentElement.getAttribute('lang') === 'en'
        ? 'Collaboration with'
        : 'Spolupráca s';
    showcase.innerHTML = `
          <div class="project-showcase-track"></div>
          ${
            studioLogo
              ? `
            <div class="${brandClass.trim()}">
              <span class="project-showcase-brand-label lang-text" data-sk="Spolupráca s" data-en="Collaboration with">${brandLabelText}</span>
              <img src="${studioLogo.src}" class="project-showcase-logo" alt="${studioLogo.alt}" loading="lazy" decoding="async" />
            </div>
          `
              : ''
          }
          <div class="project-showcase-controls">
            <button class="project-showcase-btn prev" type="button" aria-label="Previous media"><i class="fa-solid fa-chevron-left"></i></button>
            <button class="project-showcase-btn next" type="button" aria-label="Next media"><i class="fa-solid fa-chevron-right"></i></button>
            <div class="project-showcase-dots"></div>
          </div>
        `;
    const track = showcase.querySelector('.project-showcase-track');
    const dotsWrap = showcase.querySelector('.project-showcase-dots');
    const prevBtn = showcase.querySelector('.project-showcase-btn.prev');
    const nextBtn = showcase.querySelector('.project-showcase-btn.next');
    let index = 0;
    let timer = null;
    let resumeTimer = null;
    let enabled = false;
    let touchStartX = 0;
    let touchDeltaX = 0;
    media.forEach((item, i) => {
      const slide = document.createElement('div');
      slide.className = 'project-showcase-slide';
      if (i === 0 && wrap.dataset.showcasePlaceholder) {
        slide.style.backgroundImage = `url(${JSON.stringify(wrap.dataset.showcasePlaceholder)})`;
        slide.style.backgroundSize = 'cover';
        slide.style.backgroundPosition = 'center';
      }
      if (item.type === 'video') {
        const video = document.createElement('video');
        video.src = item.src;
        video.muted = true;
        video.loop = true;
        video.autoplay = false;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'none';
        video.setAttribute('aria-label', item.alt || `Project media ${i + 1}`);
        slide.appendChild(video);
      } else {
        const image = document.createElement('img');
        image.src = item.src;
        image.alt = item.alt;
        image.loading = i === 0 ? 'eager' : 'lazy';
        image.fetchPriority = i === 0 ? 'high' : 'low';
        image.decoding = 'async';
        slide.appendChild(image);
      }
      track.appendChild(slide);
      const dot = document.createElement('button');
      dot.className = 'project-showcase-dot' + (i === 0 ? ' active' : '');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Media ${i + 1}`);
      dot.addEventListener('click', () => {
        setProjectSlide(i, true);
      });
      dotsWrap.appendChild(dot);
    });
    const PROJECT_FIRST_SLIDE_MS = 3500;
    const PROJECT_SLIDE_MS = 4500;
    const dots = Array.from(showcase.querySelectorAll('.project-showcase-dot'));
    const max = media.length - 1;
    const preloadedImages = new Set();
    const preloadedVideos = new Set();
    function normalizeProjectIndex(targetIndex) {
      return targetIndex > max ? 0 : targetIndex < 0 ? max : targetIndex;
    }
    function warmProjectMedia(targetIndex) {
      const resolvedIndex = normalizeProjectIndex(targetIndex);
      const item = media[resolvedIndex];
      if (!item) return;
      if (item.type === 'image') {
        if (preloadedImages.has(item.src)) return;
        const preloadImage = new Image();
        preloadImage.decoding = 'async';
        preloadImage.loading = 'eager';
        preloadImage.src = item.src;
        preloadedImages.add(item.src);
        return;
      }
      if (preloadedVideos.has(item.src)) return;
      const slide = track.children[resolvedIndex];
      const video = slide ? slide.querySelector('video') : null;
      if (video) {
        video.preload = 'auto';
        if (video.readyState < 3) video.load();
      } else {
        const preloadVideo = document.createElement('video');
        preloadVideo.preload = 'auto';
        preloadVideo.muted = true;
        preloadVideo.playsInline = true;
        preloadVideo.src = item.src;
        preloadVideo.load();
      }
      preloadedVideos.add(item.src);
    }
    function preloadProjectNeighbors(activeIndex) {
      if (!enabled || !view.classList.contains('active-view')) return;
      [0, 1, -1, 2, -2].forEach((offset) => warmProjectMedia(activeIndex + offset));
    }
    function syncProjectMediaPlayback(activeIndex) {
      Array.from(track.children).forEach((slide, idx) => {
        const isActive = idx === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        const video = slide.querySelector('video');
        if (!video) return;
        if (isActive && enabled && view.classList.contains('active-view')) {
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } else {
          video.pause();
        }
      });
    }
    function applyProjectTransform(animate = true) {
      if (!animate) {
        track.classList.add('no-transition');
        void track.offsetWidth;
      }
      dots.forEach((dot, i) => dot.classList.toggle('active', i === index));
      syncProjectMediaPlayback(index);
      if (!animate) {
        track.classList.remove('no-transition');
      }
    }
    function setProjectSlide(nextIndex, stopTimer = false, animate = true) {
      const resolvedIndex = normalizeProjectIndex(nextIndex);
      const previousIndex = index;
      stopAuto();
      index = resolvedIndex;
      preloadProjectNeighbors(resolvedIndex);
      if (!animate || resolvedIndex === previousIndex) {
        applyProjectTransform(false);
        if (stopTimer) scheduleAutoResume();
        else startAuto();
        return;
      }
      applyProjectTransform(true);
      if (stopTimer) scheduleAutoResume();
      else startAuto();
    }
    function goProjectNext(stopTimer = false) {
      if (media.length < 2) return;
      setProjectSlide(index + 1, stopTimer, true);
    }
    function goProjectPrev(stopTimer = false) {
      if (media.length < 2) return;
      setProjectSlide(index - 1, stopTimer, true);
    }
    function startAuto(delay = PROJECT_SLIDE_MS) {
      if (!enabled || !view.classList.contains('active-view') || timer || media.length < 2) return;
      timer = setTimeout(() => {
        timer = null;
        if (enabled && view.classList.contains('active-view')) goProjectNext();
      }, delay);
    }
    function stopAuto() {
      if (!timer) return;
      clearTimeout(timer);
      timer = null;
    }
    function clearAutoResume() {
      if (!resumeTimer) return;
      clearTimeout(resumeTimer);
      resumeTimer = null;
    }
    function scheduleAutoResume() {
      stopAuto();
      clearAutoResume();
      if (!enabled || !view.classList.contains('active-view')) return;
      resumeTimer = setTimeout(() => {
        resumeTimer = null;
        startAuto();
      }, 10000);
    }
    function restartAuto() {
      if (!view.classList.contains('active-view')) return;
      enabled = true;
      stopAuto();
      clearAutoResume();
      preloadProjectNeighbors(index);
      applyProjectTransform(false);
      startAuto(index === 0 ? PROJECT_FIRST_SLIDE_MS : PROJECT_SLIDE_MS);
    }
    if (prevBtn) prevBtn.addEventListener('click', () => goProjectPrev(true));
    if (nextBtn) nextBtn.addEventListener('click', () => goProjectNext(true));
    showcase.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].clientX;
        touchDeltaX = 0;
      },
      { passive: true },
    );
    showcase.addEventListener(
      'touchmove',
      (e) => {
        touchDeltaX = e.changedTouches[0].clientX - touchStartX;
      },
      { passive: true },
    );
    showcase.addEventListener(
      'touchend',
      () => {
        const threshold = 45;
        if (touchDeltaX > threshold) goProjectPrev(true);
        else if (touchDeltaX < -threshold) goProjectNext(true);
      },
      { passive: true },
    );
    setProjectSlide(0, false, false);
    wrap.insertBefore(showcase, wrap.firstChild);
    preferences.applyLangTextElements(showcase);
    return {
      resume: restartAuto,
      pause() {
        enabled = false;
        stopAuto();
        clearAutoResume();
        syncProjectMediaPlayback(index);
      },
    };
  };

  projects.initProjectCardHoverPanels = function initProjectCardHoverPanels(root = document) {
    root.querySelectorAll('.project-card').forEach((card) => {
      const warmCover = () =>
        projects.warmShowcaseCover(
          document.getElementById(`view-project-${card.dataset.projectId}`),
        );
      card.addEventListener('pointerenter', warmCover, { once: true });
      card.addEventListener('focusin', warmCover, { once: true });
      card.addEventListener('touchstart', warmCover, { once: true, passive: true });
      if (card.querySelector(':scope > .project-card-overlay')) return;
      const imageWrapper = card.querySelector(':scope > .image-wrapper');
      if (!imageWrapper) return;
      const overlay = document.createElement('div');
      overlay.className = 'project-card-overlay';
      Array.from(card.children).forEach((child) => {
        if (child === imageWrapper) return;
        if (child.classList.contains('project-number')) return;
        overlay.appendChild(child);
      });
      const logoImg = imageWrapper.querySelector('.project-preview-logo img');
      if (logoImg) {
        const logo = document.createElement('span');
        logo.className = 'project-card-overlay-logo';
        logo.appendChild(logoImg.cloneNode(true));
        overlay.appendChild(logo);
      }
      card.appendChild(overlay);
    });
  };

  projects.initProjectImageLightbox = function initProjectImageLightbox() {
    const lightbox = document.getElementById('project-lightbox');
    const lightboxImage = document.getElementById('project-lightbox-image');
    const lightboxClose = document.getElementById('project-lightbox-close');
    const lightboxPrev = document.getElementById('project-lightbox-prev');
    const lightboxNext = document.getElementById('project-lightbox-next');
    if (!lightbox || !lightboxImage || !lightboxClose || !lightboxPrev || !lightboxNext) return;
    let opener;
    let currentImages = [];
    let currentIndex = 0;
    const normalizeIndex = (index) => {
      if (!currentImages.length) return 0;
      return ((index % currentImages.length) + currentImages.length) % currentImages.length;
    };
    const renderCurrentImage = () => {
      if (!currentImages.length) return;
      const item = currentImages[normalizeIndex(currentIndex)];
      lightboxImage.src = item.src;
      lightboxImage.alt = item.alt || '';
    };
    const updateNavVisibility = () => {
      const hidden = currentImages.length < 2;
      lightboxPrev.hidden = hidden;
      lightboxNext.hidden = hidden;
    };
    const openLightbox = (images, startIndex, button) => {
      opener = button;
      currentImages = images;
      currentIndex = normalizeIndex(startIndex);
      renderCurrentImage();
      updateNavVisibility();
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lightbox-open');
      lightboxClose.focus({ preventScroll: true });
    };
    const closeLightbox = () => {
      opener?.focus({ preventScroll: true });
      opener = null;
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lightbox-open');
      setTimeout(() => {
        if (lightbox.classList.contains('is-open')) return;
        lightboxImage.removeAttribute('src');
        lightboxImage.alt = '';
        currentImages = [];
        currentIndex = 0;
      }, 280);
    };
    projects.closeProjectImageLightbox = closeLightbox;
    projects.isProjectImageLightboxOpen = () => lightbox.classList.contains('is-open');
    const stepLightbox = (direction) => {
      if (currentImages.length < 2) return;
      currentIndex = normalizeIndex(currentIndex + direction);
      renderCurrentImage();
    };
    document.querySelectorAll('.detail-gallery').forEach((gallery) => {
      gallery.addEventListener('click', event => {
        const button = event.target.closest('.gallery-photo');
        if (!button) return;
        const buttons = [...gallery.querySelectorAll('.gallery-photo')];
        const images = buttons.map(({ firstElementChild: entry }) => ({
          src: entry.currentSrc || entry.src,
          alt: entry.alt || '',
        }));
        openLightbox(images, buttons.indexOf(button), button);
      });
    });
    lightboxClose.addEventListener('click', closeLightbox);
    lightboxPrev.addEventListener('click', () => stepLightbox(-1));
    lightboxNext.addEventListener('click', () => stepLightbox(1));
    let touchStart;
    lightboxImage.addEventListener('touchstart', event => {
      touchStart = event.touches.length === 1 ? event.touches[0] : null;
    }, { passive: true });
    lightboxImage.addEventListener('touchend', event => {
      if (!touchStart) return;
      const dx = event.changedTouches[0].clientX - touchStart.clientX;
      const dy = event.changedTouches[0].clientY - touchStart.clientY;
      touchStart = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) stepLightbox(dx < 0 ? 1 : -1);
    }, { passive: true });
    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) {
        closeLightbox();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && lightbox.classList.contains('is-open')) {
        closeLightbox();
        return;
      }
      if (!lightbox.classList.contains('is-open')) return;
      if (event.key === 'Tab') {
        const last = lightboxNext.hidden ? lightboxClose : lightboxNext;
        if (document.activeElement === (event.shiftKey ? lightboxClose : last)) {
          event.preventDefault();
          (event.shiftKey ? last : lightboxClose).focus();
        }
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepLightbox(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepLightbox(1);
      }
    });
  };

  projects.setCollaboratorLabel = function setCollaboratorLabel(metaEl, collaborator) {
    if (!metaEl || !collaborator) return;
    let label = metaEl.querySelector('.meta-studio');
    if (!label) {
      label = document.createElement('span');
      metaEl.appendChild(label);
    }
    const sk = `Spolupráca s ${collaborator}`;
    const en = `Collaboration with ${collaborator}`;
    label.className = 'meta-studio lang-text';
    label.setAttribute('data-sk', sk);
    label.setAttribute('data-en', en);
    label.textContent = preferences.currentLang === 'en' ? en : sk;
  };

  projects.applyProjectCollaboratorLabels = function applyProjectCollaboratorLabels() {
    document.querySelectorAll('.project-card').forEach((card) => {
      const collaborator = projects.getProjectCollaborator(card.dataset.projectId);
      if (!collaborator) return;
      projects.setCollaboratorLabel(card.querySelector('.project-meta'), collaborator);
    });
  };

}

/** Initializers are called explicitly, in dependency order, from main. */
export function initializeProjects(app) {
  const { projects, layout, preferences } = app;
  projects.initProjectCardHoverPanels();
  projects.initProjectImageLightbox();
}
